<template>
  <div
    class="ai-context-chip-list"
    :class="{
      'ai-context-chip-list--has-overflow': hasMore,
      'ai-context-chip-list--expanded': expanded && hasMore,
    }"
    :data-testid="testId"
  >
    <button
      v-if="hasMore"
      type="button"
      class="ai-context-chip-list__chip ai-context-chip-list__chip--overflow ai-context-chip-list__overflow-toggle"
      :aria-expanded="expanded"
      :aria-label="expanded ? 'Collapse chips' : `${overflowCount} more`"
      :data-testid="`${testId}__toggle`"
      @click.stop="toggleExpanded"
    >
      <span
        v-if="expanded"
        class="ai-context-chip-list__overflow-icon"
        aria-hidden="true"
      >−</span>
      <span
        v-else
        class="ai-context-chip-list__overflow-label"
        aria-hidden="true"
      >+{{ overflowCount }}</span>
    </button>

    <div class="ai-context-chip-list__chips d-flex flex-wrap gap-2">
      <span
        v-for="(chip, index) in previewChips"
        :key="chipKey(chip, index)"
        class="ai-context-chip-list__chip"
        :title="chipLabel(chip)"
        :data-testid="`${testId}__chip-${index}`"
      >
        <span class="ai-context-chip-list__chip-label">{{ chipLabel(chip) }}</span>
        <button
          v-if="removable"
          type="button"
          class="ai-context-chip-list__chip-remove"
          :aria-label="`Remove ${chipLabel(chip)}`"
          @click.stop="removeChip(index)"
        >
          ×
        </button>
      </span>

      <span
        v-if="!chips.length && showPlaceholder"
        class="ai-context-chip-list__placeholder"
        :data-testid="`${testId}__placeholder`"
      >
        {{ placeholder }}
      </span>
    </div>

    <v-expand-transition>
      <div
        v-if="expanded && overflowChips.length"
        class="ai-context-chip-list__overflow-panel"
        :data-testid="`${testId}__overflow-panel`"
      >
        <div class="ai-context-chip-list__overflow-chips d-flex flex-wrap gap-2">
          <span
            v-for="(chip, index) in overflowChips"
            :key="chipKey(chip, limit + index)"
            class="ai-context-chip-list__chip"
            :title="chipLabel(chip)"
            :data-testid="`${testId}__overflow-chip-${index}`"
          >
            <span class="ai-context-chip-list__chip-label">{{ chipLabel(chip) }}</span>
            <button
              v-if="removable"
              type="button"
              class="ai-context-chip-list__chip-remove"
              :aria-label="`Remove ${chipLabel(chip)}`"
              @click.stop="removeChip(limit + index)"
            >
              ×
            </button>
          </span>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import { formatGlChipLabel, normalizePickerId } from '../../../utils/aiContextHelpers';

export default {
  name: 'AiContextChipList',
  props: {
    chips: { type: Array, default: () => [] },
    type: { type: String, default: 'gl' },
    removable: { type: Boolean, default: false },
    showPlaceholder: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    testId: { type: String, default: 'ai-context-chip-list' },
    /** Max chips when collapsed (table view). Use 0 to always show all. */
    limit: { type: Number, default: 2 },
  },
  data() {
    return { expanded: false };
  },
  computed: {
    hasMore() {
      return this.limit > 0 && (this.chips || []).length > this.limit;
    },
    previewChips() {
      const list = this.chips || [];
      if (!this.hasMore) return list;
      return list.slice(0, this.limit);
    },
    overflowChips() {
      const list = this.chips || [];
      if (!this.hasMore) return [];
      return list.slice(this.limit);
    },
    overflowCount() {
      return this.overflowChips.length;
    },
  },
  watch: {
    chips() {
      this.expanded = false;
    },
  },
  methods: {
    chipKey(chip, index) {
      const raw = chip?.gl_id || chip?.network_id || chip?.code || chip?.name || index;
      return normalizePickerId(raw) || String(raw);
    },
    chipLabel(chip) {
      if (!chip) return '';
      if (this.type === 'gl') return formatGlChipLabel(chip);
      return (chip.name || chip.text || chip.code || chip.network || '').toString().trim();
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    removeChip(index) {
      this.$emit('remove', index);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AiContextChipList.scss';
</style>
